import type { ReactNode } from "react";
import { cn } from "../lib/cn";

/**
 * Empty state, design system spec. Two forms:
 *
 *   - quiet  · dashed mist-dark border, Newsreader italic message, optional
 *              mono caption underneath. Used by /plan when the audit trail
 *              is empty, or wherever "silence is information".
 *
 *   - feature · richer card with an icon badge + bold headline + supporting
 *               body + an optional actions row. Used by /attention when the
 *               user hasn't shaped a plan yet, or any "no data yet, here's
 *               what to do" surface.
 *
 * The variant is inferred from props: pass `icon` or `headline` to get the
 * feature treatment, otherwise the quiet form renders.
 */
export interface EmptyStateProps {
  /** Quiet-form body content (italic Newsreader). */
  children?: ReactNode;
  /** Quiet-form caption (small sans, gray-400). */
  caption?: ReactNode;
  /** Feature-form icon. When set, the feature treatment renders. */
  icon?: ReactNode;
  /** Optional icon-badge tone. Defaults to teal. */
  iconTone?: "teal" | "leaf" | "amber";
  /** Feature-form headline. */
  headline?: ReactNode;
  /** Feature-form supporting paragraph. */
  body?: ReactNode;
  /** Feature-form action row (slot). */
  actions?: ReactNode;
  className?: string;
}

const ICON_TONE: Record<NonNullable<EmptyStateProps["iconTone"]>, string> = {
  teal: "bg-surface-secondary text-ink-brand",
  leaf: "bg-surface-accent-leaf text-ink-accent-leaf",
  amber: "bg-attention-timely-bg text-attention-timely-ink",
};

export function EmptyState({
  children,
  caption,
  icon,
  iconTone = "teal",
  headline,
  body,
  actions,
  className,
}: EmptyStateProps) {
  const isFeature = Boolean(icon || headline);

  if (isFeature) {
    return (
      <article
        className={cn(
          "flex flex-col items-start gap-4 rounded-2xl border border-dashed border-border-strong bg-cream/30 p-7",
          className,
        )}
      >
        {icon ? (
          <span
            className={cn("grid h-10 w-10 place-items-center rounded-full", ICON_TONE[iconTone])}
          >
            {icon}
          </span>
        ) : null}
        {headline ? (
          <h3 className="m-0 font-title text-2xl font-medium leading-[1.2] text-ink-primary">
            {headline}
          </h3>
        ) : null}
        {body ? (
          <p className="m-0 max-w-[48ch] text-sm leading-relaxed text-ink-secondary">{body}</p>
        ) : null}
        {actions ? <div className="mt-1 flex flex-wrap items-center gap-3">{actions}</div> : null}
      </article>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-xl border border-dashed border-border-strong px-5 py-4 font-sans italic text-ink-secondary",
        className,
      )}
    >
      <div className="text-lg leading-snug">{children}</div>
      {caption ? (
        <div className="not-italic font-sans text-xs font-medium text-ink-quaternary">
          {caption}
        </div>
      ) : null}
    </div>
  );
}
